import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { surveyKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import {
  getSurveyContract,
  listSurveysContract,
  type GetSurveyResponse,
  type ListSurveysRequest,
  type ListSurveysResponse,
  type Survey
} from '@bliks-io/contracts';

// Re-export types for convenience
export type { Survey };

/**
 * Fetch a single survey by ID
 *
 * Returns survey details with populated user information.
 *
 * @example
 * ```tsx
 * function SurveyDetailsPage({ surveyId }: { surveyId: string }) {
 *   const { data, isLoading } = useSurvey(surveyId);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!data?.survey) return <div>Survey not found</div>;
 *
 *   return <SurveyDisplay survey={data.survey} />;
 * }
 * ```
 */
export function useSurvey(surveyId: string) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: surveyKeys.detail(surveyId),
    queryFn: async ({ signal }): Promise<GetSurveyResponse> => {
      const path = getSurveyContract.path.replace(':id', surveyId);
      const { data } = await apiClient.get<GetSurveyResponse>(path, { signal });
      return data;
    },
    enabled: !!token && !!surveyId,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}

/**
 * Fetch all pending surveys for the current user
 *
 * Returns surveys that haven't been completed yet, typically shown in the My Tasks page.
 *
 * @example
 * ```tsx
 * function PendingSurveysList() {
 *   const { data, isLoading } = usePendingSurveys();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!data?.surveys.length) return <EmptyState />;
 *
 *   return (
 *     <ul>
 *       {data.surveys.map(survey => (
 *         <SurveyCard key={survey._id} survey={survey} />
 *       ))}
 *     </ul>
 *   );
 * }
 * ```
 */
export function usePendingSurveys() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: surveyKeys.pending(),
    queryFn: async ({ signal }): Promise<ListSurveysResponse> => {
      const params = new URLSearchParams({
        status: 'pending'
      });
      const path = `${listSurveysContract.path}?${params.toString()}`;
      const { data } = await apiClient.get<ListSurveysResponse>(path, { signal });
      return data;
    },
    enabled: !!token,
    // Data becomes stale after 1 minute to keep task count fresh
    staleTime: 60 * 1000
  });
}

/**
 * Fetch the current user's surveys with custom filters
 *
 * The filters only narrow the caller's own surveys — the endpoint is scoped to
 * the requester.
 *
 * @example
 * ```tsx
 * function SurveysList() {
 *   const { data, isLoading } = useSurveys({
 *     stepRecording: 'recording-id',
 *     status: 'completed'
 *   });
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!data?.surveys.length) return <EmptyState />;
 *
 *   return <SurveyTable surveys={data.surveys} />;
 * }
 * ```
 */
export function useSurveys(filters?: ListSurveysRequest) {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: surveyKeys.list(filters || {}),
    queryFn: async ({ signal }): Promise<ListSurveysResponse> => {
      const params = new URLSearchParams();
      if (filters?.stepRecording) params.append('stepRecording', filters.stepRecording);
      if (filters?.status) params.append('status', filters.status);

      const path =
        filters && Object.keys(filters).length > 0
          ? `${listSurveysContract.path}?${params.toString()}`
          : listSurveysContract.path;

      const { data } = await apiClient.get<ListSurveysResponse>(path, { signal });
      return data;
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}
